import { useId, useState } from 'react';
import {
  Button,
  HelperText,
  IconButton,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextInput,
} from '@c2fo/liquidity';
import { PlusIcon, SearchIcon, TrashCanIcon } from '@c2fo/liquidity/icons';
import skipBandBriefcase from '@/enterprise/assets/skipbandbriefcase.svg?url';
import { MANUAL_CUSTOMERS_CONTENT, MONTHLY_INVOICE_VOLUMES, monthlyInvoiceVolumeLabel } from './content';
import { QuoteEntity, findQuoteEntities } from './quoteEntityCatalog';
import { AddedCustomer, MonthlyInvoiceVolumeId, customerKey } from './quoteWizardState';

interface ManualCustomersStepProps {
  customers: AddedCustomer[];
  onAdd: (customer: AddedCustomer) => void;
  /** Takes a `customerKey`, not an entity id — a typed customer has no entity id. */
  onRemove: (key: string) => void;
}

export const ManualCustomersStep = ({ customers, onAdd, onRemove }: ManualCustomersStepProps) => {
  const [query, setQuery] = useState('');
  // Null while the name is only typed.
  const [selectedEntity, setSelectedEntity] = useState<QuoteEntity | null>(null);
  const [volumeId, setVolumeId] = useState<MonthlyInvoiceVolumeId | ''>('');

  const suggestionsId = useId();

  // A selection fills the field with its own name, which would otherwise keep matching itself and
  // leave the list open over the form.
  const suggestions = selectedEntity ? [] : findQuoteEntities(query);
  const hasNoMatches = !selectedEntity && query.trim().length > 0 && suggestions.length === 0;

  const pendingName = selectedEntity ? selectedEntity.name : query.trim();
  const isDuplicate =
    pendingName.length > 0 && customers.some((customer) => customerKey(customer.name) === customerKey(pendingName));
  // The catalog is a convenience, not a gate: a name it does not have is sent to Mondu as typed.
  const canAdd = pendingName.length > 0 && !isDuplicate;

  const handleSelect = (entity: QuoteEntity) => {
    setSelectedEntity(entity);
    setQuery(entity.name);
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setSelectedEntity(null);
  };

  const handleAdd = () => {
    if (!canAdd) return;

    onAdd({
      entityId: selectedEntity?.id ?? null,
      name: pendingName,
      addressLine: selectedEntity?.addressLine ?? null,
      volumeId: volumeId === '' ? null : volumeId,
    });

    setQuery('');
    setSelectedEntity(null);
    setVolumeId('');
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="space-y-6 rounded-xl bg-white p-6 shadow-sm">
        <div className="space-y-1">
          <h2 className="m-0 p-0 text-xl font-bold text-text-primary">{MANUAL_CUSTOMERS_CONTENT.heading}</h2>
          <p className="m-0 p-0 text-sm text-text-secondary">{MANUAL_CUSTOMERS_CONTENT.description}</p>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="customerEntityName">{MANUAL_CUSTOMERS_CONTENT.fields.entityName.label}</Label>
            {/* Liquidity 6.1.2 has no combobox. `SearchInput` is the closer fit but copies `value`
                into internal state at mount and never syncs, so a selection could not update it. */}
            <div className="relative">
              <TextInput
                id="customerEntityName"
                className="w-full"
                startIcon={SearchIcon}
                aria-controls={suggestions.length > 0 ? suggestionsId : undefined}
                aria-autocomplete="list"
                autoComplete="off"
                hasError={isDuplicate}
                value={query}
                onChange={(event) => handleQueryChange(event.target.value)}
              />
              {suggestions.length > 0 && (
                <div
                  id={suggestionsId}
                  role="listbox"
                  aria-label={MANUAL_CUSTOMERS_CONTENT.fields.entityName.label}
                  className="absolute inset-x-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-lg border border-stroke bg-white py-1 shadow-md"
                >
                  {suggestions.map((entity) => (
                    // Buttons, so each suggestion is focusable and activates on Enter for free.
                    <button
                      key={entity.id}
                      type="button"
                      role="option"
                      aria-selected={false}
                      className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-gray-100"
                      onClick={() => handleSelect(entity)}
                    >
                      <span className="text-sm font-medium text-text-primary">{entity.name}</span>
                      <span className="text-xs text-text-secondary">{entity.addressLine}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {isDuplicate ? (
              <HelperText variant="error">{MANUAL_CUSTOMERS_CONTENT.errors.duplicate}</HelperText>
            ) : (
              <HelperText>
                {hasNoMatches
                  ? MANUAL_CUSTOMERS_CONTENT.fields.entityName.noResults
                  : MANUAL_CUSTOMERS_CONTENT.fields.entityName.helper}
              </HelperText>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="monthlyInvoiceVolume">{MANUAL_CUSTOMERS_CONTENT.fields.monthlyVolume.label}</Label>
            <Select value={volumeId} onValueChange={(value) => setVolumeId(value as MonthlyInvoiceVolumeId)}>
              <SelectTrigger id="monthlyInvoiceVolume" aria-label={MANUAL_CUSTOMERS_CONTENT.fields.monthlyVolume.label}>
                <SelectValue placeholder={MANUAL_CUSTOMERS_CONTENT.fields.monthlyVolume.placeholder} />
              </SelectTrigger>
              <SelectContent>
                {MONTHLY_INVOICE_VOLUMES.map(({ id, label }) => (
                  <SelectItem key={id} value={id}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <HelperText>{MANUAL_CUSTOMERS_CONTENT.fields.monthlyVolume.helper}</HelperText>
          </div>
        </div>

        {/* Via `icon`, not children: Button wraps all children in one span, so a child icon would
            stack above the label instead of sitting beside it. */}
        <Button variant="tertiary" icon={<PlusIcon className="fill-current" />} disabled={!canAdd} onClick={handleAdd}>
          {MANUAL_CUSTOMERS_CONTENT.actions.addCustomer}
        </Button>

        {customers.length > 0 && (
          <div className="space-y-3 border-t border-stroke pt-6">
            <h3 className="m-0 p-0 text-lg font-bold text-text-primary">
              {`${MANUAL_CUSTOMERS_CONTENT.addedCustomers.heading} (${customers.length})`}
            </h3>
            <ul className="m-0 list-none space-y-3 p-0">
              {customers.map((customer) => {
                const volumeLabel = monthlyInvoiceVolumeLabel(customer.volumeId);

                return (
                  <li
                    key={customerKey(customer.name)}
                    className="flex items-center justify-between gap-4 rounded-lg bg-blue-50 p-4"
                  >
                    <div className="min-w-0">
                      <p className="m-0 truncate p-0 text-sm font-medium text-text-primary">{customer.name}</p>
                      <p className="m-0 truncate p-0 text-xs text-text-secondary">
                        {customer.addressLine ?? MANUAL_CUSTOMERS_CONTENT.addedCustomers.unmatched}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {volumeLabel && <span className="text-sm text-text-primary">{volumeLabel}</span>}
                      {/* IconButton sets `fill` on the icon itself, so a className cannot recolor it:
                          `destructive` + `inverse` is the only pairing that fills red. It also sets
                          a white background, which `bg-transparent` overrides. */}
                      <IconButton
                        className="bg-transparent"
                        icon={TrashCanIcon}
                        variant="destructive"
                        inverse
                        name={`${MANUAL_CUSTOMERS_CONTENT.actions.remove} ${customer.name}`}
                        onClick={() => onRemove(customerKey(customer.name))}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Last in the card, so it sits under the Added Customers list once there is one. */}
        <p className="m-0 p-0 text-center text-xs italic text-text-secondary">{MANUAL_CUSTOMERS_CONTENT.disclaimer}</p>
      </div>

      {/* Text only — the footer's Generate Quote is the way out, so this band has no CTA of its own.
          It stays visible with a non-empty list, since it also reads as "I'm done adding". */}
      <div
        data-testid="add-customers-skip-band"
        className="flex items-center gap-6 rounded-2xl border border-dashed border-stroke bg-white p-4"
      >
        <img src={skipBandBriefcase} alt="" className="shrink-0" />
        <div className="min-w-0">
          <p className="m-0 p-0 text-lg font-bold leading-normal text-text-primary">
            {MANUAL_CUSTOMERS_CONTENT.skip.heading}
          </p>
          <p className="m-0 p-0 text-base leading-normal text-text-secondary">{MANUAL_CUSTOMERS_CONTENT.skip.text}</p>
        </div>
      </div>
    </div>
  );
};
